// src/stores/authStore.ts
import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import type { User } from '@/types/User';
import api from '@/services/api'; // Axios instance configured with baseURL and interceptors

export const useAuthStore = defineStore('authStore', () => {
    // State
    const user = ref<User | null>(null);
    const isAuthenticated = ref<boolean>(false);
    const isLoading = ref<boolean>(false);
    const error = ref<string | null>(null);

    // Actions
    const fetchCurrentUser = async () => {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await api.get<User>('/user/me'); // Endpoint to fetch current user
            user.value = response.data;
            isAuthenticated.value = true;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                // Handle specific Axios errors
                error.value = err.response?.data?.message || err.message;
            } else {
                error.value = 'An unexpected error occurred.';
            }
            isAuthenticated.value = false;
            user.value = null;
            console.error('Error fetching current user:', err);
        } finally {
            isLoading.value = false;
        }
    };

    const logout = async () => {
        isLoading.value = true;
        error.value = null;
        try {
            await api.post('/auth/logout'); // Endpoint to logout
            user.value = null;
            isAuthenticated.value = false;
            // Optionally, redirect to login page or show a message
        } catch (err) {
            if (axios.isAxiosError(err)) {
                error.value = err.response?.data?.message || err.message;
            } else {
                error.value = 'An unexpected error occurred during logout.';
            }
            console.error('Error during logout:', err);
        } finally {
            isLoading.value = false;
        }
    };

    return {
        // State
        user,
        isAuthenticated,
        isLoading,
        error,
        // Actions
        fetchCurrentUser,
        logout,
    };
});